import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import landDots from "./landDots.json";

// Wireframe globe with real continents (dotted land) and attack arcs fed
// from threats.json (public malicious-IP feed, refreshed by a GitHub Action).
// Falls back to synthetic arcs when no feed is reachable. Drag to rotate.
const R = 2;

// live feed lives on main and is refreshed independently of site deploys;
// same-origin copy is the fallback for the deployed snapshot.
const FEED_URLS = [
  "https://raw.githubusercontent.com/drewjordan414/drewjordan.dev/main/public/threats.json",
  "/threats.json",
];

const cssColor = (name, fallback) =>
  new THREE.Color(
    (
      getComputedStyle(document.documentElement).getPropertyValue(name) ||
      fallback
    ).trim()
  );

// lat/lng (degrees) -> point on sphere of given radius
const latLngToVec3 = (lat, lng, radius) => {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
};

// raised great-circle-ish arc between two surface points
const arcPoints = (a, b, segments = 64) => {
  const mid = a.clone().add(b).normalize();
  const lift = 1.14 + a.distanceTo(b) / (R * 10);
  const ctrl = mid.multiplyScalar(R * lift);
  return new THREE.QuadraticBezierCurve3(a, ctrl, b).getPoints(segments);
};

export const ThreatGlobe = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const accent = cssColor("--accent", "#3fb950");
    const accent2 = cssColor("--accent-2", "#58a6ff");

    // varied arc palette (GitHub accent hues); hot = high-severity, stands out red
    const arcColors = [
      accent,
      accent2,
      new THREE.Color("#bc8cff"), // purple
      new THREE.Color("#f778ba"), // pink
      new THREE.Color("#e3b341"), // amber
      new THREE.Color("#39c5cf"), // teal
    ];
    const hotColor = new THREE.Color("#f85149"); // red

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    camera.position.set(0, 0.4, 6.3);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const globe = new THREE.Group();
    scene.add(globe);

    // opaque core occludes the far side (real depth)
    globe.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(R * 0.985, 48, 32),
        new THREE.MeshBasicMaterial({ color: 0x0a0f0d })
      )
    );

    // faint lat/long shell for structure
    globe.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(R, 36, 24),
        new THREE.MeshBasicMaterial({
          color: accent,
          wireframe: true,
          transparent: true,
          opacity: 0.07,
        })
      )
    );

    // continents: real land points from Natural Earth
    const landVerts = [];
    const landSources = [];
    for (let i = 0; i < landDots.length; i += 2) {
      const v = latLngToVec3(landDots[i], landDots[i + 1], R * 1.002);
      landVerts.push(v.x, v.y, v.z);
      landSources.push(v);
    }
    const landGeo = new THREE.BufferGeometry();
    landGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(landVerts, 3)
    );
    globe.add(
      new THREE.Points(
        landGeo,
        new THREE.PointsMaterial({
          color: accent,
          size: 0.045,
          transparent: true,
          opacity: 0.5,
          depthWrite: false,
        })
      )
    );

    // arc endpoints — populated from the feed; synthetic land pairs until then
    let feed = []; // [{ a:Vec3, b:Vec3, hot:bool }]
    const randLand = () =>
      landSources[(Math.random() * landSources.length) | 0];
    const pickPair = () => {
      if (feed.length) {
        const f = feed[(Math.random() * feed.length) | 0];
        return { a: f.a, b: f.b, hot: f.hot };
      }
      return { a: randLand(), b: randLand(), hot: Math.random() > 0.6 };
    };

    const ARC_COUNT = 32;
    const arcs = [];
    const spawnArc = (arc) => {
      if (arc.line) {
        arc.line.geometry.dispose();
        globe.remove(arc.line);
      }
      const { a, b, hot } = pickPair();
      const geo = new THREE.BufferGeometry().setFromPoints(arcPoints(a, b));
      geo.setDrawRange(0, 0);
      arc.line = new THREE.Line(
        geo,
        new THREE.LineBasicMaterial({
          color: hot
            ? hotColor
            : arcColors[(Math.random() * arcColors.length) | 0],
          transparent: true,
          opacity: 0.9,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      arc.total = geo.attributes.position.count;
      arc.progress = 0;
      arc.speed = 0.5 + Math.random() * 0.7;
      arc.state = "grow";
      globe.add(arc.line);
    };
    for (let i = 0; i < ARC_COUNT; i++) {
      const arc = {};
      spawnArc(arc);
      arc.progress = Math.random(); // stagger
      arcs.push(arc);
    }

    // pull the live feed (best effort; keeps synthetic arcs on failure)
    (async () => {
      for (const url of FEED_URLS) {
        try {
          const res = await fetch(url, { cache: "no-store" });
          if (!res.ok) continue;
          const json = await res.json();
          const list = Array.isArray(json) ? json : json.arcs;
          if (!list || !list.length) continue;
          feed = list
            .filter((d) => Number.isFinite(d.srcLat) && Number.isFinite(d.dstLat))
            .map((d) => ({
              a: latLngToVec3(d.srcLat, d.srcLng, R),
              b: latLngToVec3(d.dstLat, d.dstLng, R),
              hot: (d.sev || 0) >= 0.6,
            }));
          if (feed.length) break;
        } catch (e) {
          /* offline / blocked — synthetic arcs continue */
        }
      }
    })();

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = !reduced;
    controls.autoRotateSpeed = 0.35;

    const resize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight || w;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    const start = performance.now();
    let last = start;
    let raf = 0;
    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (!reduced) {
        const introP = Math.min((now - start) / 2600, 1);
        controls.autoRotateSpeed = 0.35 + (1 - introP) * (1 - introP) * 9;

        for (const arc of arcs) {
          if (arc.state === "grow") {
            arc.progress += arc.speed * dt;
            arc.line.geometry.setDrawRange(
              0,
              Math.min(Math.floor(arc.progress * arc.total), arc.total)
            );
            if (arc.progress >= 1) {
              arc.state = "fade";
              arc.fade = 1;
            }
          } else {
            arc.fade -= dt * 1.4;
            arc.line.material.opacity = Math.max(arc.fade, 0) * 0.9;
            if (arc.fade <= 0) spawnArc(arc);
          }
        }
      }

      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="mac3d" aria-hidden="true" />;
};
